import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Play } from "lucide-react";
import { MaskedLine, Kicker, ArrowCta } from "@/components/Reveal";
import { VideoLightbox } from "@/components/VideoLightbox";
import { scrollToSection } from "@/lib/scroll";

const FAN = [
  { src: "/videos/mr-kebab-ig.mp4", poster: "/posters/mr-kebab-ig.jpg", client: "MISTER KEBAB", platform: "INSTAGRAM", link: "https://www.instagram.com/reel/DSmxK2pDIWM/", insights: "59K+ LIKE · 318 COMMENTI", rotate: -10, z: "z-10", y: "translate-y-3", delay: 0.8 },
  { src: "/videos/drago-ramen-hero.mp4", poster: "/posters/drago-ramen-hero.jpg", client: "DRAGO RAMEN", platform: "TIKTOK", link: "https://www.tiktok.com/@dragoramen/video/7682060771970370848", insights: "238K+ VIEWS · 3.1K LIKE", rotate: 0, z: "z-20", y: "-translate-y-4", delay: 0.95 },
  { src: "/videos/vb-beauty.mp4", poster: "/posters/vb-beauty.jpg", client: "VB BEAUTY LAB ACADEMY", platform: "TIKTOK", link: "https://www.tiktok.com/@vbbeautylabacademy/video/7688337134066879777", insights: "31.8K VIEWS · 328 LIKE", rotate: 10, z: "z-10", y: "translate-y-3", delay: 1.1 },
];

const FanCard = ({ v, onOpen }) => {
  const reduce = useReducedMotion();
  return (
    <motion.button
      type="button"
      onClick={() => onOpen(v)}
      data-testid={`fan-video-${v.client.toLowerCase().replace(/\s/g, "-")}`}
      aria-label={`Guarda il video di ${v.client}`}
      className={`group relative ${v.z} ${v.y} w-[128px] shrink-0 cursor-pointer overflow-hidden rounded-2xl border-[5px] border-white bg-navy shadow-[0_28px_60px_-24px_rgba(10,18,41,0.55)] sm:w-[168px] lg:w-[196px] -mx-5 sm:-mx-8`}
      initial={{ opacity: 0, y: 80, rotate: 0 }}
      animate={{ opacity: 1, y: 0, rotate: v.rotate }}
      transition={{ duration: 0.9, delay: v.delay, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="aspect-[9/16] w-full overflow-hidden">
        <video
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
          src={v.src}
          poster={v.poster}
          muted
          loop
          playsInline
          autoPlay={!reduce}
          preload="metadata"
          aria-label={v.client}
        />
      </div>
      <div className="absolute inset-0 flex items-center justify-center bg-navy/0 transition-colors duration-300 group-hover:bg-navy/40" aria-hidden="true">
        <span className="flex h-12 w-12 scale-50 items-center justify-center rounded-full bg-white text-navy opacity-0 shadow-xl transition-all duration-300 group-hover:scale-100 group-hover:opacity-100">
          <Play className="ml-0.5 h-5 w-5 fill-current" />
        </span>
      </div>
    </motion.button>
  );
};

export const Hero = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState(null);

  return (
    <section id="home" data-testid="hero-section" className="relative overflow-hidden bg-white bg-grid-light">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 pb-16 pt-28 sm:px-8 lg:grid-cols-12 lg:gap-4 lg:pb-20 lg:pt-36">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}>
            <Kicker>360° Marketing — Social-First</Kicker>
          </motion.div>

          <h1 className="mt-6 font-display text-[12vw] font-black leading-[0.98] tracking-tight text-navy sm:text-6xl lg:text-[4rem] xl:text-[4.6rem]">
            <MaskedLine delay={0.25}>OGNI BRAND</MaskedLine>
            <MaskedLine delay={0.4}>HA UN</MaskedLine>
            <MaskedLine delay={0.55}>
              <span className="text-brand-gradient">PUNTO DI PARTENZA.</span>
            </MaskedLine>
          </h1>

          <motion.p
            className="mt-7 max-w-xl text-lg font-semibold text-navy sm:text-xl"
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.85 }}
          >
            Partiamo dai social. Costruiamo tutto ciò che serve al tuo brand.
          </motion.p>

          <motion.div
            className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center"
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 1.05 }}
          >
            <ArrowCta testId="hero-cta-consultation" onClick={() => scrollToSection("contatti")}>
              CONSULENZA GRATUITA
            </ArrowCta>
            <ArrowCta testId="hero-cta-work" variant="outline" onClick={() => navigate("/progetti")}>
              VEDI I PROGETTI
            </ArrowCta>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5" data-testid="hero-video-fan">
          <div
            className="pointer-events-none absolute left-1/2 top-1/2 h-[320px] w-[320px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-30 blur-3xl"
            style={{ background: "radial-gradient(circle, #38BDF8 0%, #2563EB 50%, #FF8A00 72%, transparent 76%)" }}
            aria-hidden="true"
          />
          <div className="relative flex items-center justify-center">
            {FAN.map((v) => (
              <FanCard key={v.src} v={v} onOpen={setActive} />
            ))}
          </div>
          <motion.p
            className="mt-6 text-center font-mono text-[10px] tracking-[0.3em] text-navy/40"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8, duration: 0.6 }}
          >
            CONTENUTI REALI DEI NOSTRI CLIENTI — TOCCA PER GUARDARE
          </motion.p>
        </div>
      </div>

      <VideoLightbox video={active} onClose={() => setActive(null)} />
    </section>
  );
};
